import { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    const u = localStorage.getItem('tt_user');
    const t = localStorage.getItem('tt_token');
    if (u && t) setCurrentUser(JSON.parse(u));
    setLoading(false);
  }, []);

  const login = async (identifier, password) => {
    const { data } = await api.post('/auth/login', { identifier, password });
    if (data.requires2FA) return { requires2FA: true, userId: data.userId };
    localStorage.setItem('tt_token', data.token);
    localStorage.setItem('tt_user',  JSON.stringify(data.user));
    setCurrentUser(data.user);
    return data.user;
  };

  const loginWithGoogle = async (credential) => {
    const { data } = await api.post('/auth/google', { credential });
    localStorage.setItem('tt_token', data.token);
    localStorage.setItem('tt_user',  JSON.stringify(data.user));
    setCurrentUser(data.user);
    return data.user;
  };

  const login2FA = async (userId, totp) => {
    const { data } = await api.post('/auth/login-2fa', { userId, totp });
    localStorage.setItem('tt_token', data.token);
    localStorage.setItem('tt_user',  JSON.stringify(data.user));
    setCurrentUser(data.user);
    return data.user;
  };

  const register = async (formData) => {
    const { data } = await api.post('/auth/register', formData);
    localStorage.setItem('tt_token', data.token);
    localStorage.setItem('tt_user',  JSON.stringify(data.user));
    setCurrentUser(data.user);
    return data.user;
  };

  const logout = (opts) => {
    localStorage.removeItem('tt_token');
    localStorage.removeItem('tt_user');
    // Explicit sign-out clears the biometric session too; idle auto-logout keeps
    // it so the user can re-enter with a fingerprint/face on the sign-in screen.
    if (!opts?.keepBiometric) {
      localStorage.removeItem('tt_bio_token');
      localStorage.removeItem('tt_bio_user');
    }
    setCurrentUser(null);
  };

  // Idle auto-logout after 30 minutes
  useEffect(() => {
    if (!currentUser) return;
    const IDLE_MS = 30 * 60 * 1000;
    let timer;
    const reset = () => { clearTimeout(timer); timer = setTimeout(() => logout({ keepBiometric: true }), IDLE_MS); };
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(timer); events.forEach(e => window.removeEventListener(e, reset)); };
  }, [currentUser?._id]); // eslint-disable-line

  const updateProfile = async (updates) => {
    const { data } = await api.put('/users/profile', updates);
    const updated  = { ...currentUser, ...data.user };
    localStorage.setItem('tt_user', JSON.stringify(updated));
    setCurrentUser(updated);
    return updated;
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get('/auth/me');
      localStorage.setItem('tt_user', JSON.stringify(data.user));
      setCurrentUser(data.user);
      return data.user;
    } catch { return currentUser; }
  };

  const sendOtp   = async (phone) => api.post('/auth/send-otp',   { phone });
  const verifyOtp = async (otp)   => {
    const { data } = await api.post('/auth/verify-otp', { otp });
    const updated  = { ...currentUser, phoneVerified: true };
    localStorage.setItem('tt_user', JSON.stringify(updated));
    setCurrentUser(updated);
    return data;
  };

  const verifyEmail = async (otp) => {
    const { data } = await api.post('/auth/verify-email', { otp });
    const updated  = { ...currentUser, emailVerified: true };
    localStorage.setItem('tt_user', JSON.stringify(updated));
    setCurrentUser(updated);
    return data;
  };

  const resendEmailOtp = async () => api.post('/auth/resend-email-otp');

  const isStudent     = currentUser?.role === 'student';
  const isTutor       = currentUser?.role === 'tutor';
  const isAdmin       = currentUser?.role === 'admin';
  const isWriter      = currentUser?.role === 'writer';
  const isLoader      = currentUser?.role === 'loader';
  const isJambStudent = currentUser?.role === 'jambStudent';

  const displayName = currentUser
    ? (currentUser.role === 'tutor' || currentUser.role === 'admin' || currentUser.role === 'writer')
      ? currentUser.preferredName || currentUser.fullName || ''
      : `${currentUser.lastName || ''} ${currentUser.firstName || ''}`.trim()
    : '';

  const avatarInitials = displayName
    ? displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '??';

  return (
    <AuthContext.Provider value={{
      currentUser, loading,
      login, login2FA, loginWithGoogle, register, logout, updateProfile, refreshUser, sendOtp, verifyOtp, verifyEmail, resendEmailOtp,
      isStudent, isTutor, isAdmin, isWriter, isLoader, isJambStudent,
      displayName, avatarInitials,
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
